import React, { useState } from 'react';

interface StepBusinessEquityProps {
  onNext: () => void;
  onBack: () => void;
  onViewChange?: (fraction: number) => void;
}

type View = 'owns-business' | 'standalone-check' | 'redirect' | 'form';
type ValuationMethod = 'estimate' | 'direct';

const INDUSTRY_MULTIPLES = [
  { value: 2, label: '2× — Service business' },
  { value: 3, label: '3× — SaaS / Recurring revenue' },
  { value: 1, label: '1× — Retail / Product' },
  { value: 4, label: '4× — Professional services' },
];

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const formatCurrency = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

const VIEW_FRACTIONS: Record<View, number> = {
  'owns-business': 0,
  'standalone-check': 0.33,
  'redirect': 0.67,
  'form': 0.67,
};

const StepBusinessEquity: React.FC<StepBusinessEquityProps> = ({ onNext, onBack, onViewChange }) => {
  const [view, setView] = useState<View>('owns-business');

  const goToView = (v: View) => {
    setView(v);
    onViewChange?.(VIEW_FRACTIONS[v]);
  };

  // Form fields
  const [businessName, setBusinessName] = useState('');
  const [ownershipPct, setOwnershipPct] = useState('100');
  const [method, setMethod] = useState<ValuationMethod>('estimate');
  const [revenue, setRevenue] = useState('');
  const [multiple, setMultiple] = useState(2);
  const [directValue, setDirectValue] = useState('');

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const ownership = Math.min(100, Math.max(0, parseFloat(ownershipPct) || 0));
  const totalEstimate = revenue ? parseFloat(revenue) * multiple : 0;
  const stakeValue = method === 'estimate'
    ? totalEstimate * (ownership / 100)
    : parseFloat(directValue) || 0;

  const resetForm = () => {
    setBusinessName('');
    setOwnershipPct('100');
    setMethod('estimate');
    setRevenue('');
    setMultiple(2);
    setDirectValue('');
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: businessName,
          category: 'Other Business Assets',
          type: 'asset',
          account_type: 'business',
          institution: '',
          currency: 'CAD',
          value: stakeValue,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to add business equity');
      }

      setSuccessMessage(`✅ ${businessName} added!`);
      setTimeout(() => {
        resetForm();
        onNext();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Screen 1: Do you own a business? ─────────────────────────────────────
  if (view === 'owns-business') {
    return (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-white/40 hover:text-white transition-colors mb-6 self-start"
        >
          ← Back
        </button>

        <h1 className="text-3xl font-bold text-white mb-2">Do you own a business?</h1>
        <p className="text-base text-white/50 mb-8">
          Your ownership stake in a business is part of your net worth — and most finance apps ignore it entirely.
        </p>

        <div className="rounded-xl border border-white/10 bg-white/5 p-5 mb-8">
          <p className="text-xs text-white/40 uppercase tracking-widest mb-3">This includes things like</p>
          <ul className="space-y-2">
            {[
              { icon: '🏢', label: 'An incorporated business you run' },
              { icon: '🤝', label: 'A stake in a business with partners' },
              { icon: '📊', label: 'A minority share in a company' },
            ].map(({ icon, label }) => (
              <li key={label} className="flex items-center gap-3">
                <span className="text-xl w-7 shrink-0">{icon}</span>
                <p className="text-sm font-medium text-white">{label}</p>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          onClick={() => goToView('standalone-check')}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors self-start"
        >
          Yes, I own a business →
        </button>

        <button
          type="button"
          onClick={() => {
            localStorage.setItem('wealthini_no_business', 'true');
            onNext();
          }}
          className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-8 py-4 font-medium text-base transition-colors self-start mt-3"
        >
          No, I don't own a business
        </button>

        <button
          type="button"
          onClick={onNext}
          className="text-base text-white/40 hover:text-white transition-colors mt-4 self-start"
        >
          Skip for now
        </button>
      </div>
    );
  }

  // ── Screen 2: Does it have standalone value? ──────────────────────────────
  if (view === 'standalone-check') {
    return (
      <div className="flex flex-col">
        <h1 className="text-3xl font-bold text-white mb-2">Would this business keep running if you stepped away?</h1>
        <p className="text-base text-white/50 mb-8">
          Think: employees, recurring customers, systems that don't depend on you personally.
        </p>

        <div className="flex flex-col gap-3 mb-4">
          <button
            type="button"
            onClick={() => goToView('form')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors text-left"
          >
            Yes, it could run without me →
          </button>
          <button
            type="button"
            onClick={() => goToView('redirect')}
            className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-8 py-4 font-medium text-base transition-colors text-left"
          >
            No, it's essentially just me
          </button>
        </div>

        <button
          type="button"
          onClick={() => goToView('owns-business')}
          className="text-base text-white/40 hover:text-white transition-colors mt-2 self-start"
        >
          ← Back
        </button>
      </div>
    );
  }

  // ── Screen 2b: Redirect for personal-service businesses ──────────────────
  if (view === 'redirect') {
    return (
      <div className="flex flex-col">
        <h1 className="text-3xl font-bold text-white mb-2">You're already covered.</h1>
        <p className="text-base text-white/50 mb-8">
          When a business runs on you personally, its net worth value is simply what it holds — not what it earns.
        </p>

        <div className="rounded-xl border border-white/10 bg-white/5 p-5 mb-8 space-y-3">
          <p className="text-sm text-white">What counts:</p>
          <ul className="space-y-2">
            {[
              'Cash and retained earnings inside the corporation',
              'Investments held by the company',
              'Equipment or other assets the company owns',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="text-purple-400 mt-0.5 shrink-0">✦</span>
                <p className="text-sm text-white/70">{item}</p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-white/40 pt-1">
            You can add these any time from the Assets page.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            localStorage.setItem('wealthini_no_business', 'true');
            onNext();
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors self-start"
        >
          Got it, continue →
        </button>

        <button
          type="button"
          onClick={() => goToView('standalone-check')}
          className="text-base text-white/40 hover:text-white transition-colors mt-4 self-start"
        >
          ← Back
        </button>
      </div>
    );
  }

  // ── Screen 3: Estimator form ──────────────────────────────────────────────
  return (
    <div className="flex flex-col">
      <h1 className="text-3xl font-bold text-white mb-2">Add your business stake</h1>
      <p className="text-base text-white/50 mb-8">
        A rough estimate is fine — you can update this any time.
      </p>

      <form onSubmit={handleSubmit} className="rounded-xl border border-white/10 bg-white/5 p-5 mb-4 flex flex-col gap-5">
        {successMessage ? (
          <div className="text-center py-2 space-y-2">
            <p className="text-green-400 text-base">{successMessage}</p>
            <p className="text-sm text-white/40">You can add additional businesses from the Assets page after setup.</p>
          </div>
        ) : (
          <>
            {/* Business name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-white/60">Business name</label>
              <input
                type="text"
                required
                placeholder="e.g. Acme Corp"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-base text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500"
              />
            </div>

            {/* Ownership % */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-white/60">Your ownership stake</label>
              <div className="flex items-center border border-white/10 rounded-lg overflow-hidden focus-within:border-purple-500">
                <input
                  type="number"
                  required
                  min="1"
                  max="100"
                  step="1"
                  placeholder="100"
                  value={ownershipPct}
                  onChange={(e) => setOwnershipPct(e.target.value)}
                  className="flex-1 bg-transparent px-3 py-2.5 text-base text-white placeholder:text-white/20 focus:outline-none"
                />
                <span className="px-3 py-2.5 text-base text-white/40 bg-white/5 border-l border-white/10">%</span>
              </div>
            </div>

            {/* Valuation method toggle */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-white/60">How would you like to value it?</label>
              <div className="flex rounded-lg bg-white/5 p-0.5 gap-0.5">
                {([['estimate', 'Estimate it'], ['direct', "I know the value"]] as [ValuationMethod, string][]).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setMethod(val)}
                    className={`flex-1 text-sm py-2 rounded-md transition-colors ${
                      method === val ? 'bg-white/15 text-white font-medium' : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Estimate fields */}
            {method === 'estimate' && (
              <>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-white/60">Annual revenue of the whole business</label>
                  <div className="flex items-center border border-white/10 rounded-lg overflow-hidden focus-within:border-purple-500">
                    <span className="px-3 py-2.5 text-base text-white/40 bg-white/5 border-r border-white/10">CAD $</span>
                    <input
                      type="number"
                      required
                      min="0"
                      step="1"
                      placeholder="0"
                      value={revenue}
                      onChange={(e) => setRevenue(e.target.value)}
                      className="flex-1 bg-transparent px-3 py-2.5 text-base text-white placeholder:text-white/20 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-white/60">Industry multiple</label>
                  <select
                    value={multiple}
                    onChange={(e) => setMultiple(Number(e.target.value))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-base text-white focus:outline-none focus:border-purple-500"
                  >
                    {INDUSTRY_MULTIPLES.map((m) => (
                      <option key={m.value} value={m.value} className="bg-[#0a0a0f]">
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-white/30">
                    {revenue
                      ? <>e.g. {formatCurrency(parseFloat(revenue))} × {multiple}× = ~{formatCurrency(parseFloat(revenue) * multiple)} total business value</>
                      : <>e.g. $500,000 revenue × {multiple}× = ~{formatCurrency(500000 * multiple)} total business value</>
                    }
                  </p>
                </div>

                {revenue && (
                  <div className="rounded-lg bg-white/5 border border-white/10 px-4 py-3 space-y-1">
                    <div className="flex justify-between text-sm text-white/50">
                      <span>Total business value</span>
                      <span>{formatCurrency(totalEstimate)}</span>
                    </div>
                    <div className="flex justify-between text-base font-semibold text-white">
                      <span>Your {ownership}% stake</span>
                      <span className="text-purple-300">{formatCurrency(stakeValue)}</span>
                    </div>
                    <p className="text-xs text-white/30 pt-1">Rough estimate — update any time in Assets.</p>
                  </div>
                )}
              </>
            )}

            {/* Direct entry */}
            {method === 'direct' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-white/60">Value of your {ownership}% stake</label>
                <div className="flex items-center border border-white/10 rounded-lg overflow-hidden focus-within:border-purple-500">
                  <span className="px-3 py-2.5 text-base text-white/40 bg-white/5 border-r border-white/10">CAD $</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={directValue}
                    onChange={(e) => setDirectValue(e.target.value)}
                    className="flex-1 bg-transparent px-3 py-2.5 text-base text-white placeholder:text-white/20 focus:outline-none"
                  />
                </div>
              </div>
            )}

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => goToView('standalone-check')}
                className="text-base text-white/40 hover:text-white transition-colors"
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-lg text-base font-medium transition-colors disabled:opacity-50"
              >
                {submitting ? 'Adding…' : 'Add this business →'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default StepBusinessEquity;
